// hooks/finance/useRevenueChart.js
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reactQueryFinanceDefaults } from './queryDefaults';
import apiService from '../../services/api';
import { ENDPOINTS } from '../../services/endpoints';

const useRevenueChart = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const [dateRange, setDateRange] = useState(() => ({
    start_date: `${currentYear}-01-01`,
    end_date: `${currentYear}-12-31`,
    preset: 'full-year'
  }));

  const fetchRevenueChart = async () => {
    const revenueResponse = await apiService.get(ENDPOINTS.FINANCE_REVENUE_BY_MONTH, {
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
      include_previous_year: true
    });

    // ✅ Add use_cache=false to bypass backend cache
    const datesResponse = await apiService.get(ENDPOINTS.FINANCE_MAX_DATES, {
      use_cache: false,  // ✅ Force fresh data from BigQuery
      _t: Date.now()     // ✅ Cache buster for any HTTP caching
    });
    
    let max_update_date = null;
    if (datesResponse && datesResponse.max_date_updated) {
      max_update_date = datesResponse.max_date_updated;
    }

    const currentPeriodData = revenueResponse?.current_period?.monthly_data || [];
    const previousYearData = revenueResponse?.previous_year_data || [];

    // Create lookups by month number for BOTH current and previous year
    const currentDataLookup = {};
    currentPeriodData.forEach(month => {
      const monthNum = month.month_num || month.month;
      currentDataLookup[monthNum] = month;
    });

    const prevYearLookup = {};
    previousYearData.forEach(month => {
      const monthNum = month.month_num || month.month;
      prevYearLookup[monthNum] = month;
    });

    // ALWAYS create full 12-month dataset
    const months = [
      { num: 1, name: 'January', short: 'Jan' },
      { num: 2, name: 'February', short: 'Feb' },
      { num: 3, name: 'March', short: 'Mar' },
      { num: 4, name: 'April', short: 'Apr' },
      { num: 5, name: 'May', short: 'May' },
      { num: 6, name: 'June', short: 'Jun' },
      { num: 7, name: 'July', short: 'Jul' },
      { num: 8, name: 'August', short: 'Aug' },
      { num: 9, name: 'September', short: 'Sep' },
      { num: 10, name: 'October', short: 'Oct' },
      { num: 11, name: 'November', short: 'Nov' },
      { num: 12, name: 'December', short: 'Dec' },
    ];

    const year = new Date(dateRange.end_date).getFullYear();

    const chartData = months.map(monthInfo => {
      const monthNum = monthInfo.num;
      const currentMonth = currentDataLookup[monthNum];
      const previousMonth = prevYearLookup[monthNum];

      return {
        month: `${monthInfo.short} ${year}`,
        month_num: monthNum,
        month_name: monthInfo.name,
        year: year,
        
        retainer_revenue: currentMonth?.retainer_revenue || 0,
        project_revenue: currentMonth?.project_revenue || 0,
        other_revenue: currentMonth?.other_revenue || 0,
        revenue: currentMonth?.revenue || 0,
        
        last_year_revenue: previousMonth?.revenue || 0,
        last_year_retainer: previousMonth?.retainer_revenue || 0,
        last_year_project: previousMonth?.project_revenue || 0,
        last_year_other: previousMonth?.other_revenue || 0,
        
        transaction_count: currentMonth?.transaction_count || 0,
        avg_transaction_amount: currentMonth?.avg_transaction_amount || 0,
      };
    });

    return {
      chartData,
      summaryStats: revenueResponse?.summary || {},
      yoyComparison: revenueResponse?.year_over_year_comparison || {},
      lastUpdated: max_update_date
    };
  };

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['revenueChart', dateRange.start_date, dateRange.end_date],
    queryFn: fetchRevenueChart,
    ...reactQueryFinanceDefaults,
  });

  return {
    chartData: data?.chartData || [],
    summaryStats: data?.summaryStats || {},
    yoyComparison: data?.yoyComparison || {},
    
    loading: isLoading,
    refreshing: isFetching,
    
    error: isError ? (error?.message || 'Failed to load finance data') : null,
    isError,
    
    initialized: !!data,
    lastUpdated: data?.lastUpdated || null,
    
    dateRange,
    setDateRange,
    
    refetch,
  };
};

export { useRevenueChart };